import { motion, useInView } from "motion/react";
import { ReactNode, useRef } from "react";

interface Props {
  children: ReactNode;
  className?: string;
  /** Intensity of the adaptive warm glow when in view (0–1) */
  intensity?: number;
}

/**
 * Wraps a section with subtle viewport-aware emphasis.
 * When centered in viewport, a very soft warm atmospheric layer fades in.
 * When out of view, it fades back — creating an invisible "breathing" effect.
 */
const AdaptiveSection = ({ children, className = "", intensity = 1 }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    margin: "-25% 0px -25% 0px",
    amount: 0.05,
  });

  const glowOpacity = intensity * 0.04;

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Adaptive atmospheric warmth — fades in when section is centered */}
      <motion.div
        className="absolute inset-0 pointer-events-none z-0"
        initial={false}
        animate={{
          opacity: isInView ? 1 : 0,
        }}
        transition={{
          duration: isInView ? 2.2 : 3.0,
          ease: [0.22, 0.68, 0.35, 1],
        }}
      >
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,var(--glow-color)_0%,transparent_70%)]"
          style={{
            "--glow-color": `hsl(32 35% 52% / ${glowOpacity})`,
          } as React.CSSProperties}
        />
      </motion.div>
      <div className="relative z-[1]">{children}</div>
    </div>
  );
};

export default AdaptiveSection;
